import { CallbackOffers } from './components/Callbacks';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';
import { api, getToken, setToken, whenUnauthorized } from './lib/api';
import { connectRealtime, disconnectRealtime, whenRevoked } from './lib/realtime';
import { loadReference } from './lib/reference';
import { softphone } from './lib/softphone';
import { chime } from './lib/sounds';
import { myCall, useNova } from './lib/store';
import type { AuthUser } from './lib/types';
import { CommandPalette } from './components/CommandPalette';
import { Login } from './components/Login';
import { Ops } from './components/Ops';
import { PausedBanner, Rail, TopBar } from './components/Shell';
import { Workspace } from './components/Workspace';
import { AdminView } from './components/admin/AdminView';
import { Home } from './components/Home';
import { ReportsHub } from './components/ReportsHub';

function decode(token: string): AuthUser | null {
  try {
    const body = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (body.exp && body.exp * 1000 < Date.now()) return null;
    return { sub: body.sub, org: body.org, slug: body.slug, role: body.role, name: body.name };
  } catch {
    return null;
  }
}

export function App() {
  const user = useNova((s) => s.user);

  // Restore a session from this tab
  useEffect(() => {
    const token = getToken();
    const u = token ? decode(token) : null;
    if (u) useNova.getState().set({ user: u, view: landingView(u.role) });
    else setToken(null);
    whenUnauthorized(() => signOut(false));
    whenRevoked((message) => {
      signOut(false);
      useNova.getState().toast(message, 'error');
    });
  }, []);

  // Start everything once signed in
  useEffect(() => {
    if (!user) return;
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    (async () => {
      const store = useNova.getState();
      const ok = await loadReference();
      if (cancelled || !ok) return;
      connectRealtime(token);
      // Ask for the microphone up front so the first call is not delayed by the prompt
      navigator.mediaDevices
        ?.getUserMedia({ audio: true })
        .then((s) => s.getTracks().forEach((t) => t.stop()))
        .catch(() => store.toast('Allow the microphone in your browser to take calls', 'error'));
      void softphone.start();
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // A call (ringing, live, or in wrap-up) always brings Work forward, from Home or anywhere else
  useEffect(
    () =>
      useNova.subscribe((s, prev) => {
        if (s.view === 'cockpit') return;
        const call = myCall(s);
        const offered = Object.values(s.chats).some((c) => c.status === 'ringing' && prev.chats[c.interactionId]?.status !== 'ringing');
        if (offered) chime('alert');
        const arrived = (s.ringing && !prev.ringing) || (call && call.interactionId !== myCall(prev)?.interactionId) || (s.wrap && !prev.wrap) || offered;
        if (arrived) useNova.getState().set({ view: 'cockpit' });
      }),
    [],
  );

  // Earcons for the moments that matter
  useEffect(
    () =>
      useNova.subscribe((s, prev) => {
        const me = s.user?.sub;
        const latest = s.feed[0];
        if (!latest || latest === prev.feed[0]) return;
        const d = latest.data as { agentId?: string };
        if (latest.type === 'call.answered' && d.agentId === me) chime('connect');
        if (latest.type === 'call.ended' && d.agentId === me) chime('end');
        if (latest.type === 'call.queued' && s.user?.role !== 'agent' && s.view === 'ops') chime('alert');
        // Quality: a review of my work, or (reviewers) an agent disputing one
        if (latest.type === 'qa.reviewed' && d.agentId === me) {
          const q = latest.data as { score?: number | null; passed?: boolean | null };
          s.toast(`New quality review: ${q.score ?? '–'}% (${q.passed ? 'pass' : 'fail'}). See Reports > Quality > My reviews.`);
        }
        if (latest.type === 'qa.disputed' && (latest.data as { reviewerId?: string }).reviewerId === me) s.toast(`${(latest.data as { agentName?: string }).agentName ?? 'An agent'} disputed a review. See Reports > Quality > Reviews.`);
        if (latest.type === 'email.assigned' && d.agentId === me) {
          chime('connect');
          s.toast(`New email: ${(latest.data as { subject?: string }).subject ?? ''}`);
        }
      }),
    [],
  );

  if (!user) {
    return (
      <>
        <div className="nova-grid" />
        <Login />
        <Toasts />
      </>
    );
  }

  return (
    <div className="flex h-dvh flex-col">
      <div className="nova-grid" />
      <TopBar onSignOut={() => signOut()} />
      <PausedBanner />
      <div className="flex min-h-0 flex-1">
        <Rail />
        <main className="min-w-0 flex-1 pb-14 md:pb-0">
          <CurrentView />
        </main>
      </div>
      <CommandPalette />
      <CallbackOffers />
      <Toasts />
    </div>
  );
}

function CurrentView() {
  const view = useNova((s) => s.view);
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={view}
        className={['cockpit', 'admin', 'reports', 'history', 'recordings'].includes(view) ? 'h-full' : 'h-full overflow-auto p-4 md:p-6'}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      >
        {view === 'cockpit' ? (
          <Workspace />
        ) : view === 'home' ? (
          <Home />
        ) : view === 'ops' ? (
          <Ops />
        ) : view === 'admin' ? (
          <AdminView />
        ) : (
          // Reports, the call log and recordings live together in the Reports hub
          <ReportsHub start={view === 'history' ? 'calllog' : view === 'recordings' ? 'recordings' : view === 'quality' ? 'quality' : undefined} />
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export function landingView(role: AuthUser['role']) {
  if (role === 'agent') return 'home' as const;
  if (role === 'admin' || role === 'platform_admin') return 'admin' as const;
  // QA work in recordings
  if (role === 'qa') return 'recordings' as const;
  return 'ops' as const;
}

/** Ends the session. `sessionValid` false skips telling the server (it already refused us). */
export function signOut(sessionValid = true) {
  if (!useNova.getState().user) return;
  const me = useNova.getState().me;
  if (sessionValid && me && me.state !== 'on_call') void api('PUT', '/agents/me/state', { state: 'offline' }).catch(() => undefined);
  // Frees this person's license at once, rather than a couple of minutes after the page closes
  if (sessionValid) void api('POST', '/auth/logout').catch(() => undefined);
  softphone.stop();
  disconnectRealtime();
  setToken(null);
  useNova.getState().reset();
}

function Toasts() {
  const toasts = useNova((s) => s.toasts);
  return (
    <div className="pointer-events-none fixed right-5 top-16 z-[60] flex w-[min(380px,calc(100vw-2.5rem))] flex-col gap-2" aria-live="polite">
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="glass pointer-events-auto rounded-xl px-4 py-3 text-sm"
            style={{ boxShadow: `inset 3px 0 0 ${t.tone === 'error' ? 'var(--color-break)' : t.tone === 'success' ? 'var(--color-available)' : 'var(--color-ion)'}` }}
            role={t.tone === 'error' ? 'alert' : 'status'}
          >
            {t.text}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
